/**
 * Cutscene data for all nights
 * Contains intro and outro cutscenes for each night
 * Story follows Ralph Hardwell's journey to save his daughter Jessica
 */

import type { Cutscene } from '../../components/Cutscenes/CutsceneManager';

// Helper function to create SVG data URI
const svgToDataUri = (svg: string): string => {
  // Remove newlines and extra spaces, then encode
  const cleaned = svg.replace(/\n\s*/g, '').replace(/>\s+</g, '><');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(cleaned)}`;
};

// Phone portrait with happy face - Phone Guy recordings
const PHONE_PORTRAIT = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMyMjIiLz48cmVjdCB4PSIyNSIgeT0iMTUiIHdpZHRoPSI1MCIgaGVpZ2h0PSI3MCIgcng9IjgiIGZpbGw9IiMzMzMiIHN0cm9rZT0iIzAwYWFmZiIgc3Ryb2tlLXdpZHRoPSIzIi8+PHJlY3QgeD0iMzAiIHk9IjIwIiB3aWR0aD0iNDAiIGhlaWdodD0iNTUiIGZpbGw9IiMxMTEiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjQ1IiByPSIxNSIgZmlsbD0iI2ZmZmYwMCIvPjxjaXJjbGUgY3g9IjQ0IiBjeT0iNDIiIHI9IjIiIGZpbGw9IiMwMDAiLz48Y2lyY2xlIGN4PSI1NiIgY3k9IjQyIiByPSIyIiBmaWxsPSIjMDAwIi8+PHBhdGggZD0iTSA0MiA1MCBRIDUwIDU1IDU4IDUwIiBzdHJva2U9IiMwMDAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjUwIiBjeT0iODAiIHI9IjMiIGZpbGw9IiMwMGFhZmYiLz48L3N2Zz4=";

// Ralph portrait - simple silhouette
const RALPH_PORTRAIT = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMxYTFhMmUiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIyMCIgZmlsbD0iIzRhNGE2YSIvPjxlbGxpcHNlIGN4PSI1MCIgY3k9Ijc1IiByeD0iMjUiIHJ5PSIyMCIgZmlsbD0iIzRhNGE2YSIvPjwvc3ZnPg==";

// Marisa portrait - wife on phone
const MARISA_PORTRAIT = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMyYTFhM2UiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjM1IiByPSIyMCIgZmlsbD0iIzZhNGE3YSIvPjxlbGxpcHNlIGN4PSI1MCIgY3k9Ijc1IiByeD0iMjUiIHJ5PSIyMCIgZmlsbD0iIzZhNGE3YSIvPjxwYXRoIGQ9Ik0gMzAgMjUgUSA1MCAxMCA3MCAyNSIgc3Ryb2tlPSIjOGE2YTlhIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiLz48L3N2Zz4=";

// Glitched robot portrait for Night 4 voices
const GLITCH_PORTRAIT = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMxMTAiLz48cmVjdCB4PSIxMCIgeT0iMjAiIHdpZHRoPSI4MCIgaGVpZ2h0PSI2MCIgZmlsbD0iIzMzMCIgb3BhY2l0eT0iMC41Ii8+PHRleHQgeD0iNTAiIHk9IjU1IiBmb250LXNpemU9IjQwIiBmaWxsPSIjZjAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj4/Pz88L3RleHQ+PC9zdmc+";

// ============================================
// BACKGROUND IMAGES FOR CUTSCENES
// ============================================

// Pizzeria entrance at night - dark and ominous
const BG_PIZZERIA_ENTRANCE = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#0a0a1a"/>
  <rect y="300" width="1920" height="780" fill="#1a1a2a"/>
  <circle cx="1700" cy="150" r="60" fill="#ffe4b5" opacity="0.3"/>
  <circle cx="100" cy="80" r="2" fill="#fff" opacity="0.5"/>
  <circle cx="300" cy="120" r="2" fill="#fff" opacity="0.4"/>
  <circle cx="500" cy="60" r="2" fill="#fff" opacity="0.6"/>
  <circle cx="1200" cy="90" r="2" fill="#fff" opacity="0.5"/>
  <circle cx="1600" cy="70" r="2" fill="#fff" opacity="0.6"/>
  <rect x="200" y="350" width="1520" height="730" fill="#252535"/>
  <rect x="300" y="450" width="150" height="200" fill="#0a0a15" stroke="#333" stroke-width="3"/>
  <rect x="550" y="450" width="150" height="200" fill="#0a0a15" stroke="#333" stroke-width="3"/>
  <rect x="1220" y="450" width="150" height="200" fill="#0a0a15" stroke="#333" stroke-width="3"/>
  <rect x="1470" y="450" width="150" height="200" fill="#0a0a15" stroke="#333" stroke-width="3"/>
  <rect x="810" y="550" width="300" height="530" fill="#1a1a1a" stroke="#444" stroke-width="4"/>
  <circle cx="1050" cy="820" r="12" fill="#665500"/>
  <rect x="500" y="220" width="920" height="100" fill="#330000" stroke="#660000" stroke-width="4"/>
  <text x="960" y="290" font-family="Impact" font-size="60" fill="#ff0000" text-anchor="middle">KHAN'S PIZZA PALACE</text>
  <rect x="805" y="520" width="310" height="25" fill="#ffaa00" opacity="0.15"/>
  <rect y="980" width="1920" height="100" fill="#151520"/>
</svg>
`);

// Dark hallway with animatronic shadows
const BG_INTERIOR_DARK = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#0a0a15"/>
  <rect width="350" height="1080" fill="#15151f"/>
  <rect x="1570" width="350" height="1080" fill="#15151f"/>
  <rect x="350" y="750" width="1220" height="330" fill="#1a1a25"/>
  <rect x="350" y="750" width="122" height="165" fill="#222230"/>
  <rect x="594" y="750" width="122" height="165" fill="#222230"/>
  <rect x="838" y="750" width="122" height="165" fill="#222230"/>
  <rect x="1082" y="750" width="122" height="165" fill="#222230"/>
  <rect x="1326" y="750" width="122" height="165" fill="#222230"/>
  <ellipse cx="550" cy="600" rx="100" ry="250" fill="#050508" opacity="0.8"/>
  <ellipse cx="1370" cy="600" rx="100" ry="250" fill="#050508" opacity="0.8"/>
  <circle cx="520" cy="420" r="12" fill="#ff0000" opacity="0.7"/>
  <circle cx="580" cy="420" r="12" fill="#ff0000" opacity="0.7"/>
  <circle cx="1340" cy="420" r="12" fill="#ff0000" opacity="0.7"/>
  <circle cx="1400" cy="420" r="12" fill="#ff0000" opacity="0.7"/>
  <line x1="960" y1="0" x2="960" y2="180" stroke="#333" stroke-width="4"/>
  <ellipse cx="960" cy="200" rx="50" ry="25" fill="#332200"/>
  <ellipse cx="960" cy="210" rx="35" ry="15" fill="#ffaa00" opacity="0.4"/>
  <ellipse cx="960" cy="300" rx="300" ry="150" fill="#332200" opacity="0.15"/>
</svg>
`);

// Security office with monitors
const BG_SECURITY_OFFICE = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#0a0a12"/>
  <rect x="100" y="550" width="1720" height="530" fill="#2a2a35"/>
  <rect x="100" y="550" width="1720" height="25" fill="#3a3a45"/>
  <rect x="200" y="250" width="350" height="280" fill="#1a1a20" stroke="#444" stroke-width="5"/>
  <rect x="215" y="265" width="320" height="220" fill="#111115"/>
  <rect x="600" y="250" width="350" height="280" fill="#1a1a20" stroke="#444" stroke-width="5"/>
  <rect x="615" y="265" width="320" height="220" fill="#111115"/>
  <rect x="1000" y="250" width="350" height="280" fill="#1a1a20" stroke="#444" stroke-width="5"/>
  <rect x="1015" y="265" width="320" height="220" fill="#111115"/>
  <rect x="1400" y="250" width="350" height="280" fill="#1a1a20" stroke="#444" stroke-width="5"/>
  <rect x="1415" y="265" width="320" height="220" fill="#111115"/>
  <text x="375" y="385" font-family="monospace" font-size="18" fill="#444" text-anchor="middle">CAM 01</text>
  <text x="775" y="385" font-family="monospace" font-size="18" fill="#444" text-anchor="middle">CAM 02</text>
  <text x="1175" y="385" font-family="monospace" font-size="18" fill="#444" text-anchor="middle">CAM 03</text>
  <text x="1575" y="385" font-family="monospace" font-size="18" fill="#444" text-anchor="middle">CAM 04</text>
  <rect x="1650" y="620" width="100" height="150" rx="12" fill="#222" stroke="#00aaff" stroke-width="3"/>
  <rect x="1665" y="635" width="70" height="100" fill="#111"/>
  <circle cx="1700" cy="755" r="10" fill="#00aaff" opacity="0.9"/>
  <circle cx="400" cy="700" r="70" fill="#2a2a35" stroke="#444" stroke-width="4"/>
  <line x1="400" y1="630" x2="400" y2="770" stroke="#555" stroke-width="10"/>
  <line x1="330" y1="700" x2="470" y2="700" stroke="#555" stroke-width="10"/>
  <rect x="700" y="620" width="120" height="160" fill="#ddd" transform="rotate(-5 760 700)"/>
  <rect x="760" y="630" width="120" height="160" fill="#eee" transform="rotate(3 820 710)"/>
  <ellipse cx="1000" cy="660" rx="30" ry="12" fill="#5a3a2a"/>
  <rect x="970" y="660" width="60" height="70" fill="#5a3a2a"/>
  <ellipse cx="1000" cy="730" rx="30" ry="12" fill="#4a2a1a"/>
</svg>
`);

// Morning/6 AM - dawn scene
const BG_MORNING = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#1a1a3a"/>
  <rect y="400" width="1920" height="680" fill="#2a2a4a"/>
  <rect y="700" width="1920" height="380" fill="#3a3a5a"/>
  <circle cx="960" cy="850" r="200" fill="#ffcc00" opacity="0.3"/>
  <circle cx="960" cy="850" r="150" fill="#ffdd44" opacity="0.5"/>
  <circle cx="960" cy="850" r="100" fill="#ffee66" opacity="0.7"/>
  <rect x="200" y="400" width="1520" height="680" fill="#1a1a25"/>
  <rect x="300" y="500" width="150" height="200" fill="#ffa040" opacity="0.2"/>
  <rect x="550" y="500" width="150" height="200" fill="#ffa040" opacity="0.15"/>
  <rect x="1220" y="500" width="150" height="200" fill="#ffa040" opacity="0.2"/>
  <rect x="1470" y="500" width="150" height="200" fill="#ffa040" opacity="0.15"/>
  <rect x="500" y="300" width="920" height="100" fill="#330000" stroke="#660000" stroke-width="4"/>
  <text x="960" y="370" font-family="Impact" font-size="60" fill="#ff0000" text-anchor="middle" opacity="0.7">KHAN'S PIZZA PALACE</text>
  <text x="960" y="150" font-family="monospace" font-size="100" fill="#ffcc00" text-anchor="middle" font-weight="bold">6:00 AM</text>
</svg>
`);

// Home interior - phone call scenes
const BG_HOME = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#1a1a25"/>
  <rect width="1920" height="650" fill="#2a2535"/>
  <rect y="650" width="1920" height="430" fill="#3a3545"/>
  <rect x="1350" y="120" width="450" height="450" fill="#4a5a6a" stroke="#555" stroke-width="10"/>
  <rect x="1375" y="145" width="195" height="195" fill="#7a8aaa"/>
  <rect x="1580" y="145" width="195" height="195" fill="#7a8aaa"/>
  <rect x="1375" y="350" width="195" height="195" fill="#7a8aaa"/>
  <rect x="1580" y="350" width="195" height="195" fill="#7a8aaa"/>
  <rect x="1290" y="70" width="100" height="550" fill="#4a3545"/>
  <rect x="1760" y="70" width="100" height="550" fill="#4a3545"/>
  <rect x="150" y="500" width="900" height="250" rx="25" fill="#5a4555"/>
  <rect x="150" y="440" width="900" height="100" rx="20" fill="#6a5565"/>
  <rect x="1100" y="350" width="40" height="350" fill="#444"/>
  <ellipse cx="1120" cy="330" rx="100" ry="50" fill="#ffa"/>
  <ellipse cx="1120" cy="350" rx="70" ry="25" fill="#ffcc80" opacity="0.4"/>
  <rect x="550" y="620" width="120" height="200" rx="15" fill="#222" stroke="#00aaff" stroke-width="3"/>
  <rect x="565" y="640" width="90" height="150" fill="#111"/>
</svg>
`);

// Glitched/static background - creepy moments
const BG_GLITCH = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#050505"/>
  <rect y="150" width="1920" height="8" fill="#ff0000" opacity="0.4"/>
  <rect y="350" width="1920" height="5" fill="#00ff00" opacity="0.3"/>
  <rect y="550" width="1920" height="10" fill="#0000ff" opacity="0.4"/>
  <rect y="750" width="1920" height="6" fill="#ff0000" opacity="0.3"/>
  <rect y="900" width="1920" height="4" fill="#00ffff" opacity="0.2"/>
  <rect x="100" y="100" width="80" height="60" fill="#151515"/>
  <rect x="400" y="200" width="120" height="40" fill="#101010"/>
  <rect x="800" y="150" width="60" height="90" fill="#181818"/>
  <rect x="1200" y="100" width="100" height="70" fill="#121212"/>
  <rect x="1600" y="180" width="90" height="50" fill="#141414"/>
  <rect x="200" y="450" width="150" height="35" fill="#161616"/>
  <rect x="600" y="500" width="80" height="100" fill="#111111"/>
  <rect x="1000" y="480" width="180" height="25" fill="#131313"/>
  <rect x="1400" y="520" width="70" height="80" fill="#151515"/>
  <rect x="300" y="800" width="100" height="50" fill="#121212"/>
  <rect x="700" y="850" width="140" height="30" fill="#101010"/>
  <rect x="1100" y="820" width="90" height="70" fill="#141414"/>
  <rect x="1500" y="870" width="110" height="40" fill="#131313"/>
  <circle cx="350" cy="350" r="20" fill="#ff0000" opacity="0.5"/>
  <circle cx="430" cy="350" r="20" fill="#ff0000" opacity="0.5"/>
  <circle cx="1450" cy="300" r="18" fill="#ff0000" opacity="0.4"/>
  <circle cx="1520" cy="300" r="18" fill="#ff0000" opacity="0.4"/>
  <circle cx="900" cy="650" r="22" fill="#ff0000" opacity="0.6"/>
  <circle cx="990" cy="650" r="22" fill="#ff0000" opacity="0.6"/>
  <text x="960" y="540" font-family="monospace" font-size="60" fill="#ff0000" text-anchor="middle" opacity="0.6">ERROR</text>
  <text x="970" y="550" font-family="monospace" font-size="60" fill="#00ffff" text-anchor="middle" opacity="0.3">ERROR</text>
</svg>
`);

// Happy ending epilogue - sunny day
const BG_EPILOGUE = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 1080">
  <rect width="1920" height="1080" fill="#87ceeb"/>
  <rect y="750" width="1920" height="330" fill="#228b22"/>
  <circle cx="1600" cy="200" r="120" fill="#ffdd00" opacity="0.9"/>
  <circle cx="1600" cy="200" r="150" fill="#ffdd00" opacity="0.4"/>
  <ellipse cx="300" cy="150" rx="120" ry="60" fill="#fff" opacity="0.9"/>
  <ellipse cx="400" cy="140" rx="100" ry="50" fill="#fff" opacity="0.9"/>
  <ellipse cx="900" cy="200" rx="150" ry="70" fill="#fff" opacity="0.8"/>
  <ellipse cx="1020" cy="185" rx="110" ry="55" fill="#fff" opacity="0.8"/>
  <rect x="600" y="450" width="720" height="450" fill="#d4a574"/>
  <polygon points="600,450 960,200 1320,450" fill="#8b4513"/>
  <rect x="700" y="520" width="170" height="170" fill="#87ceeb" stroke="#5a3a2a" stroke-width="10"/>
  <rect x="1050" y="520" width="170" height="170" fill="#87ceeb" stroke="#5a3a2a" stroke-width="10"/>
  <line x1="785" y1="520" x2="785" y2="690" stroke="#5a3a2a" stroke-width="5"/>
  <line x1="700" y1="605" x2="870" y2="605" stroke="#5a3a2a" stroke-width="5"/>
  <line x1="1135" y1="520" x2="1135" y2="690" stroke="#5a3a2a" stroke-width="5"/>
  <line x1="1050" y1="605" x2="1220" y2="605" stroke="#5a3a2a" stroke-width="5"/>
  <rect x="890" y="620" width="140" height="280" fill="#5a3a2a"/>
  <circle cx="1000" cy="770" r="12" fill="#ffd700"/>
  <text x="960" y="100" font-family="Georgia" font-size="48" fill="#2a5a2a" text-anchor="middle" font-style="italic">Three months later...</text>
</svg>
`);

// ============================================
// CUTSCENE DATA
// ============================================

export const nightIntros: Record<number, Cutscene> = {
  1: {
    id: 'night1_intro',
    skippable: true,
    backgroundImage: BG_PIZZERIA_ENTRANCE,
    dialogues: [
      // Ralph's internal thoughts
      {
        text: "(This is it. Khan's Pizza Palace. $500,000 for six nights...)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(Jessica needs that treatment. I have to do this for her.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(Forty years this place has been abandoned. What could possibly go wrong?)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // Phone Guy recording starts
      {
        text: "*Click* Hello? Hello, hello?",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "Uh, if you're hearing this, you must be the new security guard. Welcome to Khan's Pizza Palace!",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "I used to work this position before... well, before I decided to leave these recordings.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "So, the animatronics here... they're from the original 1987 opening. Revolutionary for their time.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "At night, they tend to... wander. Something about their servos locking up if they stay still too long.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Just check your cameras, watch the doors, and conserve power. You'll be fine.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "Oh, and uh... if they do get in, they probably won't recognize you as a person.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "They'll see you as a... well, just don't let them get close. Trust me.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Good luck! I'll check in tomorrow night. *Click*",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 1 intro complete');
    },
  },
  2: {
    id: 'night2_intro',
    skippable: true,
    backgroundImage: BG_SECURITY_OFFICE,
    dialogues: [
      {
        text: "(Night two. I made it through the first one. Those robots... they're something else.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(Found some old documents last night. Something about this place doesn't add up.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // Phone Guy recording
      {
        text: "*Click* Hey! Great job on Night 1! See, I told you it wouldn't be that bad.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "Uh, so... you might have noticed some of the older documents lying around.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "The company... they had some secrets back in '87. Things they didn't want people to know.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "I started piecing things together during my shifts. Missing children reports, sealed files...",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "But hey, that was 40 years ago, right? Nothing to worry about now.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "The robots are more active tonight. Keep your eyes on the cameras. *Click*",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 2 intro complete');
    },
  },
  3: {
    id: 'night3_intro',
    skippable: true,
    backgroundImage: BG_INTERIOR_DARK,
    dialogues: [
      {
        text: "(Those lore items... children went missing here. Multiple children.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(And there was a serial killer involved? David Bazucko... what a monster.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // Phone Guy recording
      {
        text: "*Click* Hello? Hey, you're doing great! Most people don't make it past Night 2.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "I mean... they quit! That's what I meant. They quit. Not anything else.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "So, uh, I found some old news clippings during my time there. About a serial killer.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "David Bazucko. The police got him in '97. Forty-five victims over a decade.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "He used to target kids at this very restaurant. 'Free candy' vans, fake emergencies...",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "But here's the thing... not all the missing kids were his victims.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Some of them disappeared INSIDE the restaurant. Near the animatronics room.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Just... be careful tonight. The robots know you're there. *Click*",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 3 intro complete');
    },
  },
  4: {
    id: 'night4_intro',
    skippable: true,
    backgroundImage: BG_GLITCH,
    dialogues: [
      {
        text: "(Something's different tonight. The robots... they're more aggressive.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // Phone Guy recording - more urgent
      {
        text: "*Click* Hey! Night 4. You've made it further than most.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "Listen, I need to tell you something. Something I didn't want to say before.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "Last night of my shift... I heard something. Coming from the robots.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "It wasn't their normal pre-recorded phrases. It was... different.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Voices. Children's voices. Crying out for help.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "I thought I was losing my mind. But then I found Dr. Reed's notes...",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "Something called 'Project INTEGRATION.' The robots don't just learn... they absorb.",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "I... I couldn't save them. Maybe you can find the truth. *Click*",
        speaker: "Phone Guy",
        portrait: PHONE_PORTRAIT,
        autoAdvance: false,
      },
      // The glitched voices
      {
        text: "*BZZT* H-h-help... me...",
        speaker: "???",
        portrait: GLITCH_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "*Static* Somebody... please... help me...",
        speaker: "???",
        portrait: GLITCH_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "(What... what was that?! Those voices... they're coming from the robots!)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 4 intro complete');
    },
  },
  5: {
    id: 'night5_intro',
    skippable: true,
    backgroundImage: BG_GLITCH,
    dialogues: [
      {
        text: "(The children... they're INSIDE the robots. That's what Project INTEGRATION is.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(Their consciousness... absorbed into those machines. Still aware. Still suffering.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(God, how many years have they been trapped in there?)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // No Phone Guy recording - he's gone
      {
        text: "*Static*",
        speaker: "System",
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: true,
        delay: 1500,
      },
      {
        text: "No new recordings available.",
        speaker: "System",
        sceneImage: BG_SECURITY_OFFICE,
        autoAdvance: false,
      },
      {
        text: "(Phone Guy... what happened to you?)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(I found the final documents. The Board of Directors covered everything up in December '87.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "(Richard Khan disappeared. Dr. Reed fled. Detective Morrison was 'handled.')",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "(Tomorrow is my last night. I need to end this. For Jessica... and for those children.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 5 intro complete');
    },
  },
  6: {
    id: 'night6_intro',
    skippable: true,
    backgroundImage: BG_INTERIOR_DARK,
    dialogues: [
      {
        text: "(Final night. One more shift and I can pay for Jessica's treatment.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(But I can't just leave knowing what I know. Those children deserve peace.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(There's a main power switch somewhere in the facility. I need to find it.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      {
        text: "(Shut down the power, shut down the robots. End this nightmare for good.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        autoAdvance: false,
      },
      // Glitched voices more intense
      {
        text: "*BZZT* P-please... we've been waiting... so long...",
        speaker: "???",
        portrait: GLITCH_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "*Static* It hurts... everything hurts... make it stop...",
        speaker: "???",
        portrait: GLITCH_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "*BZZT* Find... the switch... free... us...",
        speaker: "???",
        portrait: GLITCH_PORTRAIT,
        sceneImage: BG_GLITCH,
        autoAdvance: false,
      },
      {
        text: "(I hear you. I'm going to end this tonight. I promise.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 6 intro complete');
    },
  },
  7: {
    id: 'night7_intro',
    skippable: true,
    backgroundImage: BG_GLITCH,
    dialogues: [
      {
        text: "CUSTOM NIGHT",
        speaker: "System",
        autoAdvance: true,
        delay: 1500,
      },
      {
        text: "This is a bonus challenge mode.",
        speaker: "System",
        autoAdvance: false,
      },
      {
        text: "All robots are at maximum aggression.",
        speaker: "System",
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "Story mode has been completed. This is purely for skilled players.",
        speaker: "System",
        autoAdvance: false,
      },
      {
        text: "Good luck. You'll need it.",
        speaker: "System",
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 7 (Custom Night) intro complete');
    },
  },
};

export const nightOutros: Record<number, Cutscene> = {
  1: {
    id: 'night1_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "Night 1 Complete",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      // Phone call with Marisa
      {
        text: "*Ring ring* Hey honey, it's me.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph! How was your first night? Are you okay?",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I'm fine. It was... strange. But I made it through.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Jessica keeps asking when daddy's coming home. She misses you.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Tell her I'll be home soon. Just five more nights and we'll have the money.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Be careful, Ralph. I love you.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I love you too. Kiss the kids for me.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 1 outro complete');
    },
  },
  2: {
    id: 'night2_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "Night 2 Complete",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      // Phone call with Marisa
      {
        text: "Marisa? Yeah, I just got off my shift.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "You sound tired. Is everything alright?",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I found some old documents. This place... kids went missing here, back in '87.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph, that's awful. But it was so long ago...",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I know. I'm probably overthinking it. How's Jessica?",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "She's holding on. The doctor says the treatment will help once we can afford it.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Four more nights. We're almost there.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 2 outro complete');
    },
  },
  3: {
    id: 'night3_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "Night 3 Complete",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      // Phone call with Marisa - more tense
      {
        text: "Marisa, you won't believe what I found.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph, you sound shaken. What happened?",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "There was a serial killer. Targeted kids at this restaurant. David Bazucko.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Oh my God... Ralph, maybe you should quit. We can find another way.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "He's dead. Police got him in '97. But there's more... the robots...",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "The robots? What about them?",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I don't know yet. Something's not right. I need to find out more.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Please be careful. Three more nights. You can do this.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 3 outro complete');
    },
  },
  4: {
    id: 'night4_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "Night 4 Complete",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      // Phone call with Marisa - Ralph is disturbed
      {
        text: "Marisa... I... I heard something tonight.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "What is it? You sound terrified.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Voices. Children's voices. Coming from inside the robots.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph, that's... that's not possible.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "They were crying for help. 'Somebody help me.' Over and over.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Maybe you're just exhausted. The stress is getting to you.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I found documents about something called 'Project INTEGRATION.' The robots... they absorbed them.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph... two more nights. Just survive two more nights and come home to us.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I will. I promise. For Jessica. For all of you.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 4 outro complete');
    },
  },
  5: {
    id: 'night5_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "Night 5 Complete",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      // Phone call with Marisa - Ralph has made a decision
      {
        text: "Marisa, I know everything now. The full truth.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Tell me.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Those missing children from '87... they're trapped inside the robots. Their consciousness was absorbed.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "That's... that's horrifying. Those poor children...",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "The company covered it up. Destroyed evidence. 'Handled' anyone who investigated.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph, please. Just finish tomorrow and come home. Let the police handle it.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I can't just leave them like that. There's a main power switch. If I shut it down...",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "You can finally set them free. I understand. Just... come home to us.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "One more night. Tomorrow, I end this.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 5 outro complete');
    },
  },
  6: {
    id: 'night6_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "POWER SHUTDOWN COMPLETE",
        speaker: "System",
        autoAdvance: true,
        delay: 1500,
      },
      {
        text: "All Systems Offline",
        speaker: "System",
        autoAdvance: true,
        delay: 1500,
      },
      // Ralph discovers the truth
      {
        text: "(The robots... they've stopped. It's over.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "(Wait... what's that inside them? Oh God... oh God no...)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "(The children's remains... still inside. Skin torn, parts missing...)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "(Forty years. They've been trapped in there for forty years.)",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      {
        text: "*Takes photos* The police need to see this. Everyone needs to know.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_INTERIOR_DARK,
        autoAdvance: false,
      },
      // Final call with Marisa
      {
        text: "Marisa... it's done. I'm coming home.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Ralph! Thank God! Are you okay?!",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I found them, Marisa. The children. I have evidence. I'm sending it to the police.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "Come home, Ralph. Jessica is asking for you. She needs her father.",
        speaker: "Marisa",
        portrait: MARISA_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      {
        text: "I'm on my way. We got the money. Jessica's going to be okay.",
        speaker: "Ralph",
        portrait: RALPH_PORTRAIT,
        sceneImage: BG_HOME,
        autoAdvance: false,
      },
      // Epilogue
      {
        text: "THREE MONTHS LATER",
        speaker: "System",
        sceneImage: BG_EPILOGUE,
        autoAdvance: true,
        delay: 2000,
      },
      {
        text: "Jessica received her treatment for Spinal Muscular Atrophy.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "She's recovering well. The doctors say she'll live a full, healthy life.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "The police investigated Khan's Pizza Palace based on Ralph's evidence.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "Nine children were finally laid to rest after forty years.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "The Board of Directors was exposed. Justice was served.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "Ralph was promoted to CEO of Khan Security Team.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "The Hardwell family lives happily, ever after.",
        speaker: "Narrator",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "THE END",
        speaker: "System",
        sceneImage: BG_EPILOGUE,
        autoAdvance: true,
        delay: 2000,
      },
      {
        text: "Thank you for playing Five Nights at Khan's",
        speaker: "System",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
      {
        text: "Custom Night unlocked in Main Menu.",
        speaker: "System",
        sceneImage: BG_EPILOGUE,
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 6 outro complete - MAIN STORY COMPLETE');
    },
  },
  7: {
    id: 'night7_outro',
    skippable: false,
    backgroundImage: BG_MORNING,
    dialogues: [
      {
        text: "6 AM",
        speaker: "System",
        autoAdvance: true,
        delay: 1000,
      },
      {
        text: "CUSTOM NIGHT COMPLETE",
        speaker: "System",
        autoAdvance: true,
        delay: 1500,
      },
      {
        text: "You have proven yourself a true survivor.",
        speaker: "System",
        autoAdvance: false,
      },
      {
        text: "Not many can withstand all robots at maximum aggression.",
        speaker: "System",
        autoAdvance: false,
      },
      {
        text: "Congratulations. You've mastered Five Nights at Khan's.",
        speaker: "System",
        autoAdvance: false,
      },
      {
        text: "Thank you for playing!",
        speaker: "System",
        autoAdvance: false,
      },
    ],
    onComplete: () => {
      console.log('Night 7 (Custom Night) outro complete');
    },
  },
};
